import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import {
  Users, MapPin, Calendar, Wallet, Ship, Sparkles, Target,
  TrendingUp, ArrowRight, AlertTriangle, CheckCircle2, Map, UserPlus,
  FileText, TrendingDown, Loader2,
} from "lucide-react";
import { BOAT_LABELS, EXP_LABELS, fmtEur } from "@/admin/adminLabels";
import { toast } from "sonner";

const COMPAT_COLORS = {
  eccellente: { bg: "#0B1A2D", text: "#FFFFFF", label: "Cluster eccellente" },
  buono:      { bg: "#10B981", text: "#FFFFFF", label: "Cluster buono" },
  possibile:  { bg: "#F59E0B", text: "#FFFFFF", label: "Cluster possibile" },
  sconsigliato:{ bg: "#94A3B8", text: "#FFFFFF", label: "Cluster sconsigliato" },
};

const STATUS_META_CLUSTER = {
  vendibile:    { bg: "#ECFDF5", border: "#A7F3D0", text: "#065F46", label: "Vendibile ora",    icon: CheckCircle2 },
  quasi_pronto: { bg: "#FEF3C7", border: "#FCD34D", text: "#92400E", label: "Quasi pronto",     icon: TrendingUp },
  incompleto:   { bg: "#FFF7ED", border: "#FED7AA", text: "#9A3412", label: "Da completare",    icon: AlertTriangle },
};

const ACTION_ICONS = {
  users: UserPlus, ship: Ship, calendar: Calendar, map: Map, target: Target,
};
const ACTION_PRIORITY_STYLE = {
  alta:  "border-l-4 border-l-red-400  bg-red-50/60  text-red-900",
  media: "border-l-4 border-l-amber-400 bg-amber-50/60 text-amber-900",
  info:  "border-l-4 border-l-sky-300   bg-sky-50/60   text-sky-900",
};

function CompatBar({ label, value }) {
  const color = value >= 75 ? "bg-emerald-500" : value >= 60 ? "bg-amber-500" : "bg-rose-400";
  return (
    <div>
      <div className="flex items-center justify-between text-xs">
        <span className="text-ink/70">{label}</span>
        <span className="font-semibold text-navy">{value}%</span>
      </div>
      <div className="mt-1 h-1 bg-sand rounded-full overflow-hidden">
        <div className={`h-full ${color}`} style={{ width: `${value}%` }} />
      </div>
    </div>
  );
}

function ParticipantRow({ p, onClick }) {
  const exps = (p.experiences || []).map(e => EXP_LABELS[e] || e).join(", ") || "—";
  const periodLabel = p.date_mode === "precise"
    ? `${p.start_date || ""} → ${p.end_date || ""}`
    : p.date_mode === "flexible"
      ? `${p.flexible_month || ""} · ${p.flexible_duration || ""}gg`
      : "Migliori occasioni";
  return (
    <button
      data-testid={`cluster-participant-${p.id}`}
      onClick={() => onClick(p.id)}
      className="w-full text-left px-3 py-2.5 rounded-lg border border-[#E2E8F0] bg-white hover:bg-sand/60 hover:border-navy/30 transition group"
    >
      <div className="flex items-center justify-between">
        <p className="font-medium text-navy text-sm">{p.name}</p>
        <ArrowRight className="h-3.5 w-3.5 text-ink/30 group-hover:text-navy" />
      </div>
      <div className="mt-1 flex flex-wrap gap-x-3 text-xs text-ink/80">
        <span>{p.num_participants} pax</span>
        <span>{fmtEur(p.budget)}</span>
        <span>{BOAT_LABELS[p.boat_type] || p.boat_type}</span>
        <span>{periodLabel}</span>
      </div>
      <p className="mt-1 text-[10px] text-ink/60 italic">Esperienze: {exps}</p>
    </button>
  );
}

const SUGGESTION_ICONS = {
  change_boat: Ship,
  change_period: Calendar,
  change_destination: Map,
  cabin_charter: Users,
  aggregate_participants: UserPlus,
  min_budget: Wallet,
};

const SUGGESTION_TYPE_LABELS = {
  change_boat: "Cambio imbarcazione",
  change_period: "Cambio settimana",
  change_destination: "Cambio destinazione",
  cabin_charter: "Cabin Charter",
  aggregate_participants: "Aggrega partecipanti",
  min_budget: "Budget minimo",
};

function CorrectionSuggestion({ s, leadId, onProposalCreated }) {
  const Icon = SUGGESTION_ICONS[s.type] || Sparkles;
  const TypeLabel = SUGGESTION_TYPE_LABELS[s.type] || s.type;
  const [creating, setCreating] = useState(false);

  // Le suggestion "min_budget" e "aggregate_participants" non generano un preventivo immediato.
  const canGenerateProposal = !["min_budget", "aggregate_participants"].includes(s.type);

  const handleGenerate = async (e) => {
    e?.stopPropagation();
    setCreating(true);
    try {
      const res = await api.post(`/admin/leads/${leadId}/proposals/from-suggestion`, {
        suggestion_type: s.type,
        estimated_price: s.estimated_price,
        change: s.change || {},
      });
      toast.success(`Bozza preventivo generata: ${res.data.boat_model_name} · ${fmtEur(res.data.price_eur)}`);
      onProposalCreated?.(res.data);
    } catch (err) {
      toast.error("Errore nel generare la bozza preventivo");
      console.error(err);
    } finally {
      setCreating(false);
    }
  };

  const headerColor = s.feasible ? "text-emerald-900" : "text-amber-900";
  const cardColor = s.feasible
    ? "border-emerald-200 bg-emerald-50/60"
    : "border-amber-200 bg-amber-50/60";
  const iconColor = s.feasible ? "text-emerald-700" : "text-amber-700";

  return (
    <div
      data-testid={`fx-suggestion-${s.type}`}
      className={`mt-2.5 rounded-lg border p-3 ${cardColor}`}
    >
      {/* Header: tipo + titolo */}
      <div className="flex items-start gap-2.5">
        <div className={`h-7 w-7 rounded-full bg-white border ${s.feasible ? "border-emerald-200" : "border-amber-200"} flex items-center justify-center shrink-0`}>
          <Icon className={`h-3.5 w-3.5 ${iconColor}`} />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[9px] uppercase tracking-wider font-semibold text-ink/60">{TypeLabel}</p>
          <p className={`mt-0.5 text-xs font-semibold leading-snug ${headerColor}`}>{s.title}</p>
        </div>
      </div>

      {/* Rationale */}
      {s.rationale && (
        <div className="mt-2.5 flex items-start gap-1.5">
          <Sparkles className="h-3 w-3 mt-0.5 shrink-0 text-ink/40" />
          <p className="text-[11px] text-ink/80 leading-relaxed">{s.rationale}</p>
        </div>
      )}

      {/* Economic + Logistic benefits */}
      <div className="mt-2.5 grid grid-cols-1 sm:grid-cols-2 gap-2">
        {s.economic_benefit && (
          <div className="rounded-md bg-white/70 border border-emerald-100 px-2.5 py-2">
            <div className="flex items-center gap-1 text-[9px] uppercase tracking-wider font-semibold text-emerald-700">
              <TrendingDown className="h-3 w-3" />
              <span>Beneficio economico</span>
            </div>
            <p className="mt-1 text-[11px] text-ink/85 leading-snug">{s.economic_benefit.text}</p>
          </div>
        )}
        {s.logistic_benefit && (
          <div className="rounded-md bg-white/70 border border-navy/10 px-2.5 py-2">
            <div className="flex items-center gap-1 text-[9px] uppercase tracking-wider font-semibold text-navy/70">
              <CheckCircle2 className="h-3 w-3" />
              <span>Beneficio logistico</span>
            </div>
            <p className="mt-1 text-[11px] text-ink/85 leading-snug">{s.logistic_benefit}</p>
          </div>
        )}
      </div>

      {/* Price + savings row */}
      <div className="mt-2.5 flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px]">
          {s.estimated_price > 0 && (
            <span className="font-serif text-sm text-navy">≈ {fmtEur(s.estimated_price)}</span>
          )}
          {s.savings_vs_request > 0 && (
            <span className="text-emerald-700 font-semibold">−{fmtEur(s.savings_vs_request)}</span>
          )}
          {s.budget_gap > 0 && (
            <span className="text-amber-700 font-semibold">Mancano {fmtEur(s.budget_gap)}</span>
          )}
        </div>
        {canGenerateProposal && (
          <button
            data-testid={`fx-generate-proposal-${s.type}`}
            onClick={handleGenerate}
            disabled={creating}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-semibold bg-navy text-white hover:bg-[#06101f] transition disabled:opacity-60"
          >
            {creating ? <Loader2 className="h-3 w-3 animate-spin" /> : <FileText className="h-3 w-3" />}
            <span>Genera preventivo</span>
          </button>
        )}
      </div>
    </div>
  );
}

export default function ClustersPanelV2({ onSelectLead }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/admin/clusters-v2").then(r => setData(r.data)).catch(() => setData({ aggregation_clusters: [], whole_boat_leads: [], to_correct_leads: [], summary: {} }));
  }, []);

  if (!data) return <div className="text-ink">Caricamento cluster…</div>;
  const { aggregation_clusters: clusters, whole_boat_leads: wbLeads, to_correct_leads: tcLeads, summary } = data;

  return (
    <div className="space-y-8" data-testid="clusters-v2">
      <div>
        <p className="label-eyebrow">Motore cluster · Operativo</p>
        <h2 className="mt-2 font-serif text-2xl text-navy">Classificazione richieste</h2>
        <p className="mt-1 text-sm text-ink/70">
          Regola madre: prima si classifica la richiesta come <strong>Barca intera</strong> (gruppo+budget autosufficienti),
          <strong> Da aggregare</strong> (piccoli gruppi/singoli da matchare insieme) o <strong>Da correggere</strong> (budget sotto soglia).
          Solo le richieste "da aggregare" vengono raggruppate in cluster.
        </p>
        <div className="mt-4 grid grid-cols-3 gap-3">
          <div className="bg-white border border-[#E2E8F0] rounded-xl p-4">
            <p className="text-[10px] uppercase tracking-wider text-emerald-700 font-semibold">Barca intera</p>
            <p className="mt-1 font-serif text-2xl text-navy">{summary?.whole_boat_count || 0}</p>
          </div>
          <div className="bg-white border border-[#E2E8F0] rounded-xl p-4">
            <p className="text-[10px] uppercase tracking-wider text-sky-700 font-semibold">Da aggregare</p>
            <p className="mt-1 font-serif text-2xl text-navy">{summary?.aggregation_count || 0}</p>
          </div>
          <div className="bg-white border border-[#E2E8F0] rounded-xl p-4">
            <p className="text-[10px] uppercase tracking-wider text-amber-700 font-semibold">Da correggere</p>
            <p className="mt-1 font-serif text-2xl text-navy">{summary?.to_correct_count || 0}</p>
          </div>
        </div>
      </div>

      {/* AGGREGAZIONE */}
      {clusters.length > 0 && (
        <section>
          <p className="label-eyebrow">Cluster aggregazione</p>
          <h3 className="mt-2 font-serif text-xl text-navy">Gruppi da formare insieme</h3>
          <div className="mt-4 grid lg:grid-cols-2 gap-5">
            {clusters.map(c => {
              const compat = COMPAT_COLORS[c.compatibility.label] || COMPAT_COLORS.possibile;
              return (
                <article key={c.id} data-testid={`cluster-card-${c.id}`} className="bg-white border border-[#E2E8F0] rounded-2xl overflow-hidden flex flex-col">
                  <div className="px-5 py-4 border-b border-[#F1F5F9] flex items-start justify-between gap-3">
                    <div>
                      <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full bg-sky-100 text-sky-800">AGGREGAZIONE</span>
                      <div className="mt-2 flex items-center gap-2">
                        <MapPin className="h-4 w-4 text-navy" />
                        <p className="font-serif text-xl text-navy">{c.destination}</p>
                        <Calendar className="h-4 w-4 text-ink/60 ml-2" />
                        <span className="text-sm text-ink">{c.month}</span>
                      </div>
                    </div>
                    <div className="text-right">
                      <span className="text-[10px] uppercase tracking-wider font-bold px-2.5 py-1 rounded-full" style={{ background: compat.bg, color: compat.text }}>{compat.label}</span>
                      <p className="mt-2 font-serif text-2xl text-navy leading-none">{c.compatibility.final}%</p>
                    </div>
                  </div>
                  <div className="px-5 py-4 border-b border-[#F1F5F9]">
                    {/* HEADLINE COMMERCIALE */}
                    {(() => {
                      const cm = c.commercial || {};
                      const meta = STATUS_META_CLUSTER[cm.commercial_status] || STATUS_META_CLUSTER.incompleto;
                      const Icon = meta.icon;
                      return (
                        <div className="rounded-lg p-3 border" style={{ background: meta.bg, borderColor: meta.border, color: meta.text }} data-testid={`cluster-status-${c.id}`}>
                          <div className="flex items-start gap-2">
                            <Icon className="h-4 w-4 mt-0.5 shrink-0" />
                            <div className="flex-1 min-w-0">
                              <p className="text-[10px] uppercase tracking-wider font-bold">{meta.label}</p>
                              <p className="mt-1 text-xs leading-relaxed">{cm.commercial_headline || c.action_suggested}</p>
                            </div>
                          </div>
                        </div>
                      );
                    })()}

                    {/* METRICHE COMMERCIALI */}
                    <div className="mt-4 grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
                      <div>
                        <p className="text-[10px] uppercase tracking-wider text-ink/60">Partecipanti attuali</p>
                        <p className="mt-0.5 font-serif text-lg text-navy">{c.commercial?.participants_current ?? c.seats_occupied}</p>
                      </div>
                      <div>
                        <p className="text-[10px] uppercase tracking-wider text-ink/60">Budget aggregato</p>
                        <p className="mt-0.5 font-serif text-lg text-navy">{fmtEur(c.commercial?.total_budget_current ?? c.total_budget)}</p>
                      </div>
                      <div>
                        <p className="text-[10px] uppercase tracking-wider text-ink/60">Budget medio / persona</p>
                        <p className="mt-0.5 font-serif text-lg text-navy">{fmtEur(c.commercial?.avg_budget_per_person ?? Math.round((c.total_budget || 0) / Math.max(c.seats_occupied || 1, 1)))}</p>
                      </div>
                      <div>
                        <p className="text-[10px] uppercase tracking-wider text-ink/60">Prezzo target / persona</p>
                        <p className="mt-0.5 font-serif text-lg text-emerald-700">≤ {fmtEur(c.commercial?.target_price_per_person || 1000)}</p>
                      </div>

                      {/* Proiezione: solo se servono nuovi partecipanti */}
                      {c.commercial?.min_extra_participants_needed > 0 && (
                        <>
                          <div className="col-span-2 mt-1 pt-3 border-t border-dashed border-[#E2E8F0]">
                            <p className="text-[10px] uppercase tracking-wider text-amber-700 font-bold flex items-center gap-1.5">
                              <UserPlus className="h-3 w-3" /> Proiezione con +{c.commercial.min_extra_participants_needed} partecipant{c.commercial.min_extra_participants_needed === 1 ? "e" : "i"}
                            </p>
                          </div>
                          <div>
                            <p className="text-[10px] uppercase tracking-wider text-ink/60">Partecipanti minimi consigliati</p>
                            <p className="mt-0.5 font-serif text-lg text-amber-700">+{c.commercial.min_extra_participants_needed}</p>
                          </div>
                          <div>
                            <p className="text-[10px] uppercase tracking-wider text-ink/60">Budget stimato proiettato</p>
                            <p className="mt-0.5 font-serif text-base text-navy">
                              {fmtEur(c.commercial.projected_total_budget_low)}–{fmtEur(c.commercial.projected_total_budget)}
                            </p>
                          </div>
                        </>
                      )}

                      {/* SOLUZIONE CONSIGLIATA */}
                      <div className="col-span-2 mt-1 pt-3 border-t border-dashed border-[#E2E8F0]">
                        <p className="text-[10px] uppercase tracking-wider text-ink/60 font-semibold flex items-center gap-1.5">
                          <Ship className="h-3 w-3" /> Soluzione consigliata
                        </p>
                        <p className="mt-1 text-sm text-navy">
                          {BOAT_LABELS[c.commercial?.recommended_boat_type || c.prevalent_boat_type] || c.prevalent_boat_type}
                          {c.commercial?.recommended_boat_type && c.commercial.recommended_boat_type !== c.prevalent_boat_type && (
                            <span className="ml-2 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 align-middle">cambio</span>
                          )}
                        </p>
                        {c.commercial?.recommended_boat_reasons?.length > 0 && (
                          <ul className="mt-1 text-[11px] text-ink/70 space-y-0.5">
                            {c.commercial.recommended_boat_reasons.map((r, i) => <li key={i}>· {r}</li>)}
                          </ul>
                        )}
                      </div>

                      {/* COMMISSIONE PROIETTATA */}
                      <div className="col-span-2 mt-1">
                        <p className="text-[10px] uppercase tracking-wider text-ink/60">Commissione Seamatch (8% su budget aggregato attuale)</p>
                        <p className="mt-0.5 font-serif text-base text-gold">{fmtEur(c.commission_estimated)}</p>
                      </div>
                    </div>
                  </div>
                  <div className="px-5 py-4 space-y-2 border-b border-[#F1F5F9]">
                    <p className="text-[10px] uppercase tracking-wider text-ink/60 font-semibold">Compatibilità</p>
                    <CompatBar label="Budget" value={c.compatibility.budget} />
                    <CompatBar label="Tipo barca" value={c.compatibility.boat_type} />
                    <CompatBar label="Esperienza" value={c.compatibility.experience} />
                  </div>
                  <div className="px-5 py-4 bg-sand/40 border-b border-[#F1F5F9]" data-testid={`cluster-actions-${c.id}`}>
                    <p className="text-[10px] uppercase tracking-wider text-gold font-semibold flex items-center gap-1">
                      <Target className="h-3 w-3" /> Azioni consigliate
                    </p>
                    <ul className="mt-2 space-y-1.5">
                      {(c.commercial?.actions || []).map((a, i) => {
                        const Icon = ACTION_ICONS[a.icon] || Target;
                        const style = ACTION_PRIORITY_STYLE[a.priority] || ACTION_PRIORITY_STYLE.info;
                        return (
                          <li key={i} className={`px-3 py-2 rounded-md text-xs leading-relaxed flex items-start gap-2 ${style}`}>
                            <Icon className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                            <span>{a.text}</span>
                          </li>
                        );
                      })}
                      {(!c.commercial?.actions || c.commercial.actions.length === 0) && (
                        <li className="text-xs text-ink">{c.action_suggested}</li>
                      )}
                    </ul>
                  </div>
                  <div className="px-5 py-4 flex-1">
                    <p className="text-[10px] uppercase tracking-wider text-ink/60 font-semibold mb-2">Partecipanti ({c.participants.length})</p>
                    <div className="space-y-2">
                      {c.participants.map(p => <ParticipantRow key={p.id} p={p} onClick={onSelectLead} />)}
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        </section>
      )}

      {/* BARCA INTERA */}
      {wbLeads.length > 0 && (
        <section>
          <p className="label-eyebrow">Barca intera · Lavorabili singolarmente</p>
          <h3 className="mt-2 font-serif text-xl text-navy">Richieste già autonome</h3>
          <p className="mt-1 text-xs text-ink/70">Queste richieste hanno gruppo e budget sufficienti per prenotare un'intera imbarcazione. Lavorale individualmente con preventivi dedicati.</p>
          <div className="mt-4 grid md:grid-cols-2 lg:grid-cols-3 gap-3">
            {wbLeads.map(l => (
              <button key={l.id} data-testid={`wb-${l.id}`} onClick={() => onSelectLead(l.lead_id)}
                      className="text-left bg-white border border-emerald-200 rounded-xl p-4 hover:border-emerald-500 hover:shadow-sm transition">
                <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800">BARCA INTERA</span>
                <p className="mt-2 font-medium text-navy">{l.name}</p>
                <p className="text-xs text-ink/70">{l.destination} · {l.num_participants} pax · {BOAT_LABELS[l.boat_type] || l.boat_type}</p>
                <p className="mt-1 font-serif text-lg text-navy">{fmtEur(l.budget)}</p>
                <p className="mt-2 text-[10px] text-ink/60 italic">{l.classification_reason}</p>
              </button>
            ))}
          </div>
        </section>
      )}

      {/* DA CORREGGERE */}
      {tcLeads.length > 0 && (
        <section>
          <p className="label-eyebrow">Da correggere · Suggerimenti consulenziali</p>
          <h3 className="mt-2 font-serif text-xl text-navy">Richieste con alternative da proporre</h3>
          <p className="mt-1 text-xs text-ink/70">
            Budget complessivo o pro capita sotto soglia per la tipologia richiesta.
            Sotto ogni richiesta trovi 2-4 alternative consulenziali (cambio barca, cambio settimana, cambio destinazione, cabin charter) pronte da proporre al cliente.
          </p>
          <div className="mt-4 grid md:grid-cols-2 gap-4">
            {tcLeads.map(l => (
              <div key={l.id} data-testid={`fx-${l.id}`}
                   className="bg-white border border-amber-200 rounded-xl p-4 hover:border-amber-500 hover:shadow-sm transition">
                <button onClick={() => onSelectLead(l.lead_id)} className="w-full text-left" data-testid={`fx-open-${l.id}`}>
                  <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">DA CORREGGERE</span>
                  <p className="mt-2 font-medium text-navy">{l.name}</p>
                  <p className="text-xs text-ink/70">{l.destination} · {l.num_participants} pax · {BOAT_LABELS[l.boat_type] || l.boat_type}</p>
                  <p className="mt-1 font-serif text-lg text-navy">{fmtEur(l.budget)} <span className="text-xs text-ink/60">({fmtEur(l.budget_per_capita)}/persona)</span></p>
                  <p className="mt-2 text-[10px] text-amber-700 italic">{l.classification_reason}</p>
                </button>
                {l.suggestions && l.suggestions.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-amber-100" data-testid={`fx-suggestions-${l.id}`}>
                    <p className="text-[10px] uppercase tracking-wider font-semibold text-navy/70 mb-1">
                      Alternative consigliate
                    </p>
                    {l.suggestions.map((s, i) => (
                      <CorrectionSuggestion
                        key={`${s.type}-${i}`}
                        s={s}
                        leadId={l.lead_id}
                        onProposalCreated={() => onSelectLead(l.lead_id)}
                      />
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {clusters.length === 0 && wbLeads.length === 0 && tcLeads.length === 0 && (
        <div className="bg-white border border-[#E2E8F0] rounded-2xl p-12 text-center">
          <Sparkles className="h-8 w-8 mx-auto text-ink/30" />
          <p className="mt-4 font-serif text-lg text-navy">Nessuna richiesta da classificare</p>
        </div>
      )}
    </div>
  );
}
